'use client';

import React, {
  useEffect,
  useMemo,
  useState,
  MouseEvent,
  ChangeEvent,
} from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { faClipboardList } from '@fortawesome/pro-regular-svg-icons';
import {
  DataGridPremium,
  GridRowId,
  GridRowParams,
  GridSortModel,
} from '@mui/x-data-grid-premium';
import { strategyListTableColumns } from 'src/components/Tables/components/Columns/ColumnDefinitions';
import { TableCheckbox } from 'src/components/Tables/components/TableCheckbox';
import { TableColumn, TableSettings } from 'src/components/Table';
import { AddToListModal, TableFooter } from 'src/components';
import { Search } from 'src/components/PulseUI/Search';
import {
  useGetEngageStrategyConnectorQuery,
  useGetEngageStrategyListMembersQuery,
} from 'src/api/redux/strategy/engageStrategyApi';
import { useGetStrategyMetricsQuery } from 'src/api/redux/strategy';
import { useProfileStrategies } from 'src/hooks/strategy';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useLogging } from 'src/context/logging';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useSite } from 'src/context/providers/SiteProvider';
import { useDebounce } from 'use-debounce';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  resolveEncountersDisplayOptions,
  resolveEncountersMetricCode,
} from 'src/utils/strategyHelpers';
import { StrategyErrorState } from '../StrategyErrorState';
import { extractErrorMessage } from '../../utils/errorUtils';
import { useParams } from 'next/navigation';

interface StrategyProfileListProps {
  engageStrategyId: string;
}

type SortValues = {
  sort: string;
  order: 'asc' | 'desc';
  sort_metric?: string | null;
};

type PaginationValues = {
  page: number;
  pageSize: number;
};

const StrategyProfileList: React.FC<StrategyProfileListProps> = ({
  engageStrategyId,
}) => {
  const { id } = useParams<{ id: string }>();
  const log = useLogging();
  const { entityDetails } = useProfile();
  const { showEncountersTotals } = useFlags();

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 500);
  const [showAddToListDialog, setShowAddToListDialog] = useState(false);
  const [paginationValues, setPaginationValues] = useState<PaginationValues>({
    page: 0,
    pageSize: 50,
  });
  const [sortValues, setSortValues] = useState<SortValues>({
    sort: 'date_added',
    order: 'desc',
    sort_metric: null,
  });

  const {
    selectedProviders,
    setSelectedProviders,
    clearSelectedProviders,
    selectedProvider,
    setSelectedProvider,
    bounds,
    metricToggles,
  } = useProfileStrategies();

  const { closeAllButCurrent } = useDrawer();
  const { dispatch } = useSite();

  const { currentData: strategyMetrics, isFetching: strategyMetricsFetching } =
    useGetStrategyMetricsQuery(
      {
        strategyType: 'engage',
        strategyId: engageStrategyId,
      },
      {
        skip: !engageStrategyId,
      }
    );

  // All metric codes (incl. encounters siblings) drive the sort swap below.
  const allMetricCodes = React.useMemo(() => {
    const codes = new Set<string>();
    (strategyMetrics?.results || []).forEach((metric: any) =>
      metric.display_options?.forEach(
        (opt: any) => opt?.code && codes.add(opt.code)
      )
    );
    return codes;
  }, [strategyMetrics?.results]);

  // Behind the flag, cohort metrics sort by the encounters sibling (same swap as the
  // displayed value). Non-cohort metrics have no sibling → unchanged.
  const effectiveSortMetric =
    showEncountersTotals && sortValues.sort_metric
      ? resolveEncountersMetricCode(
          sortValues.sort_metric,
          allMetricCodes,
          true
        )
      : sortValues.sort_metric;

  const queryParams = {
    strategyId: engageStrategyId,
    provider_id: id,
    q: debouncedSearch,
    page: paginationValues.page + 1,
    pageSize: paginationValues.pageSize,
    sort: sortValues.sort,
    order: sortValues.order,
    ...(effectiveSortMetric && { sort_metric: effectiveSortMetric }),
    bounds: bounds
      ? {
          north: bounds.max_lat,
          south: bounds.min_lat,
          east: bounds.max_lng,
          west: bounds.min_lng,
        }
      : null,
  };

  const {
    data: listMembers,
    isFetching: listMembersFetching,
    isLoading: listMembersLoading,
    isError: listMembersError,
    error: listMembersErrorData,
  } = useGetEngageStrategyListMembersQuery(queryParams, {
    skip: !engageStrategyId || !id,
  });

  const { data: connector } = useGetEngageStrategyConnectorQuery(
    {
      strategyId: engageStrategyId,
      providerId: entityDetails?.id,
    },
    { skip: !engageStrategyId || !entityDetails?.id }
  );

  // Engage strategies never auto-refresh, so don't merge last_refreshed
  // (leaving it undefined causes isNewOpportunity to return false)
  const combinedListMembers = useMemo(() => {
    if (!listMembers) return listMembers;
    return listMembers;
  }, [listMembers]);

  // Set default sort to first metric when strategy changes
  useEffect(() => {
    if (strategyMetrics?.results?.length > 0) {
      const firstMetric = strategyMetrics.results[0];
      const firstDisplayOption = firstMetric.display_options?.[0]?.code;

      if (firstDisplayOption) {
        setSortValues({
          sort: 'metric',
          order: 'desc',
          sort_metric: firstDisplayOption,
        });
      }
    }
  }, [engageStrategyId, strategyMetrics?.results]);

  // Generate stable column definitions (only when metrics structure changes, not toggles)
  const dynamicColumns = useMemo(() => {
    if (!strategyMetrics?.results?.length) return [];

    return strategyMetrics.results
      .map((metric) => {
        // Never render a standalone "Encounters" toggle; the encounters value is
        // surfaced under the claims toggle when the flag is on, relabeled to match.
        const availableOptions = resolveEncountersDisplayOptions<{
          code: string;
          label: string;
        }>(
          metric.display_options?.map((option) => ({
            code: option.code,
            label: option.label,
          })),
          !!showEncountersTotals
        );

        // Create stable column - no toggle state passed as props
        return TableColumn?.strategyDynamicColumn(
          metric.id.toString(), // field name is metric ID
          metric.label,
          {
            metricId: metric.id.toString(),
            availableOptions,
            displayOptions: metric.display_options,
            executionParams: metric.execution_params,
          }
        );
      })
      .filter(Boolean);
  }, [strategyMetrics, showEncountersTotals]);

  // Combine static columns with dynamic metric columns
  const allColumns = useMemo(() => {
    const staticStart = strategyListTableColumns.slice(0, 5);
    const staticEnd = strategyListTableColumns.slice(5);

    return [...staticStart, ...dynamicColumns, ...staticEnd];
  }, [dynamicColumns]);

  const handleSelectionChange = (newSelection: GridRowId[]) => {
    const selectedOpportunities =
      listMembers?.results?.filter((row) =>
        newSelection.includes(row.provider_id)
      ) || [];
    setSelectedProviders(selectedOpportunities);
  };

  const handleRowClick = (params: GridRowParams) => {
    setSelectedProvider(params.row);
    dispatch({
      type: 'SET_CURRENT_PROVIDER',
      payload: { id: params.row.provider_id, type: params.row.type || null },
    });
    closeAllButCurrent('profile');

    log.event('Strategy profile list provider clicked', {
      source: 'StrategyProfileList',
      providerId: params.row.provider_id,
      providerName: params.row.name,
    });
  };

  const handlePageChange = (
    e: MouseEvent<HTMLButtonElement>,
    newPage: number
  ) => {
    setPaginationValues({ ...paginationValues, page: newPage });
    log.event('Strategy profile list page changed', {
      source: 'StrategyProfileList',
      page: newPage + 1,
      pageSize: paginationValues.pageSize,
    });
  };

  const handlePageSizeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const newPageSize = parseInt(e.target.value, 10);
    setPaginationValues({
      ...paginationValues,
      pageSize: newPageSize,
    });
    log.event('Strategy profile list page size changed', {
      source: 'StrategyProfileList',
      pageSize: newPageSize,
    });
  };

  const handleAddToLists = () => {
    setShowAddToListDialog(true);
    log.event('Strategy profile list add to list clicked', {
      source: 'StrategyProfileList',
      selectedProvidersCount: selectedProviders?.length || 0,
    });
  };

  const handleAddToListSuccess = (result: {
    addedCount: number;
    targetListName: string;
  }) => {
    log.event('Providers added to list', {
      listName: result.targetListName,
      count: result.addedCount,
    });
    clearSelectedProviders();
  };

  const handleSortChange = (model: GridSortModel) => {
    if (!model?.length) {
      return;
    }

    const { field, sort: sortDirection } = model[0];
    const order: 'asc' | 'desc' = sortDirection || 'desc';

    // Check if field is a metric ID (numeric string)
    const isMetricId = /^\d+$/.test(field);

    if (isMetricId) {
      const metricId = field;

      // Find the metric in strategyMetrics to get its display_options
      const metric = strategyMetrics?.results?.find(
        (m) => m.id.toString() === metricId
      );

      // Use the selected display_option if metric has display_options, otherwise fall back to metricToggles
      let activeOption = null;
      if (metric?.display_options?.length > 0) {
        // Get the currently selected display option for this metric
        const selectedDisplayOption = (metricToggles || {})[metricId];

        // Check if the selected option exists in this metric's display_options
        const validOption = metric.display_options.find(
          (option) => option.code === selectedDisplayOption
        );

        // Use the valid option if it exists, otherwise use the first display option
        activeOption = validOption
          ? validOption.code
          : metric.display_options[0].code;
      }

      setSortValues({
        sort: 'metric',
        sort_metric: activeOption,
        order: order,
      });

      log.event('Strategy profile list sorted', {
        source: 'StrategyProfileList',
        sortField: activeOption,
        sortOrder: order,
      });
    } else {
      // Use the field name for non-metric columns
      setSortValues({
        sort: field,
        sort_metric: null,
        order: order,
      });

      log.event('Strategy profile list sorted', {
        source: 'StrategyProfileList',
        sortField: field,
        sortOrder: order,
      });
    }
  };

  // Sort model - compute which field should show the sort indicator
  const sortModel = useMemo(() => {
    if (sortValues.sort === 'metric' && strategyMetrics?.results?.length > 0) {
      // Find which metric column corresponds to the current sort_metric
      const metricId = strategyMetrics.results
        .find((metric) => {
          const firstOption = metric.display_options?.[0]?.code;
          const selectedOption = (metricToggles || {})[metric.id.toString()];
          const activeOption = metric.display_options?.find(
            (opt) => opt.code === selectedOption
          )
            ? selectedOption
            : firstOption;
          return activeOption === sortValues.sort_metric;
        })
        ?.id.toString();

      if (metricId) {
        return [{ field: metricId, sort: sortValues.order }];
      }
    } else if (sortValues.sort !== 'metric') {
      return [{ field: sortValues.sort, sort: sortValues.order }];
    }
    return [];
  }, [sortValues, strategyMetrics?.results, metricToggles]);

  const displayedCount = Math.min(
    paginationValues.pageSize,
    listMembers?.count || 0
  );

  if (listMembersError) {
    const errorMessage = extractErrorMessage(
      listMembersErrorData,
      'An error occurred while loading the list. If this issue persists, please contact your Customer Success team member for assistance.'
    );

    return (
      <StrategyErrorState
        title="Error Loading List"
        message={errorMessage}
        icon={faClipboardList}
      />
    );
  }

  return (
    <div
      data-testid="strategy-profile-list"
      className="w-full h-160 overflow-hidden pt-4 pb-0 flex flex-col rounded-lg border border-neutral-200"
    >
      <div className="flex-1 pb-0 flex flex-col min-h-0">
        <div className="self-stretch pb-0 flex flex-col gap-3 px-6">
          {/* Header with Search and Add to List */}
          <div className="@container/filters flex justify-between items-start">
            <div className="flex-1 flex justify-start items-center gap-2">
              {/* Search Input */}
              <div className="w-60 @[800px]/filters:flex-none inline-flex flex-col justify-start items-start gap-2">
                <div className="justify-start text-black text-xs font-semibold font-['Inter'] leading-none">
                  Search
                </div>
                <Search
                  value={search}
                  onSearch={setSearch}
                  placeholder="Provider or Site Name"
                  size="sm"
                  variant="outlined"
                  className="w-full bg-white rounded-full border border-neutral-200 flex items-center gap-2"
                  sx={{
                    height: '33.5px',
                    minHeight: '33.5px',
                    padding: '0.5rem 0',
                  }}
                  slotProps={{
                    inputStyle: {
                      fontSize: '14px',
                    },
                  }}
                />
              </div>

              {/* Spacer */}
              <div className="flex-1 @[800px]/filters:hidden" />

              {/* Add to List Button */}
              <button
                className="@[600px]/filters:px-3.5 @[600px]/filters:py-2 w-10 h-10 @[600px]/filters:w-auto @[600px]/filters:h-auto bg-white rounded-full border border-neautral-200 inline-flex justify-center items-center gap-1 hover:bg-neutral-100 cursor-pointer mt-5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                onClick={handleAddToLists}
                type="button"
                aria-label="Add to List"
                disabled={!selectedProviders || selectedProviders.length === 0}
              >
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <Icon icon={faPlus} className="text-primary" size="sm" />
                </div>
                <div className="hidden @[600px]/filters:block text-primary text-sm font-semibold leading-tight whitespace-nowrap">
                  Add to List
                </div>
              </button>
            </div>
          </div>

          {/* Count */}
          <div className="flex justify-start items-center gap-1 ">
            <div className="justify-start text-primary text-lg font-bold font-['Inter'] leading-7">
              Showing {displayedCount}
            </div>
            <div className="justify-start text-primary text-lg font-normal font-['Inter'] leading-7">
              of {listMembers?.count || 0}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 min-h-0 overflow-hidden">
            <DataGridPremium
              rows={combinedListMembers?.results || []}
              columns={allColumns}
              loading={
                listMembersFetching ||
                listMembersLoading ||
                strategyMetricsFetching
              }
              checkboxSelection
              hideFooter
              rowHeight={48}
              onRowSelectionModelChange={handleSelectionChange}
              rowSelectionModel={(selectedProviders || []).map(
                (provider) => provider.provider_id
              )}
              onRowClick={handleRowClick}
              disableRowSelectionOnClick
              slots={{
                baseCheckbox: TableCheckbox,
                ...TableSettings.slots,
              }}
              getRowId={(row) => row.provider_id}
              disableColumnMenu
              autosizeOnMount
              autosizeOptions={{
                columns: dynamicColumns.map((col) => col.field),
                includeHeaders: true,
                includeOutliers: true,
              }}
              sortModel={sortModel}
              sortingOrder={['desc', 'asc']}
              disableMultipleColumnsSorting
              sortingMode="server"
              onSortModelChange={handleSortChange}
              sx={{
                border: 'none',
                borderRadius: 0,
                height: '100%',
                width: '100%',
                '& .MuiDataGrid-columnHeader:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-columnHeader:focus-within': {
                  outline: 'none',
                },
                '& .MuiDataGrid-columnHeaders': {
                  position: 'relative',
                  paddingRight: '0px',
                  '& .MuiDataGrid-columnSeparator': {
                    color: 'none',
                  },
                },
                '& .MuiDataGrid-main': {
                  overflow: 'hidden',
                  height: '100%',
                },
                '& .MuiDataGrid-virtualScroller': {
                  overflowX: 'auto !important',
                  overflowY: 'auto !important',
                  paddingRight: '8px',
                },
                '& .MuiDataGrid-columnHeadersInner': {
                  minWidth: 'max-content !important',
                },
                '& .MuiDataGrid-row': {
                  minWidth: 'max-content !important',
                },
                '& .MuiDataGrid-overlay': {
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                },
                '& .MuiDataGrid-row:hover, & .Mui-hovered': {
                  backgroundColor: 'rgba(204, 204, 194, 0.13)',
                  '&.Mui-hovered': {
                    backgroundColor: 'rgba(204, 204, 194, 0.13)',
                  },
                },
              }}
            />
          </div>
          <TableFooter
            page={paginationValues.page}
            pageSize={paginationValues.pageSize}
            rowCount={listMembers?.count || 0}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      </div>

      <AddToListModal
        isOpen={showAddToListDialog}
        onClose={() => setShowAddToListDialog(false)}
        mode="add"
        providers={selectedProviders}
        onSuccess={handleAddToListSuccess}
      />
    </div>
  );
};

export default StrategyProfileList;
